import React, { Fragment, useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash-es';

import EmptyBox from './EmptyBox';
import TotalCount from './TotalCount';
import MediaObject from '../../../MediaObject';
import { SortableTable } from '../../../SortableTable';
import { skeletonTable } from '../../../Skeleton';

import { openAppQuickView } from '../../actions';
import { getMetricName, valueFormatter } from '../../configs/pub-quickview';
import { format } from '../../../../utils/date';
import helpers, { isUnified, parseUnifiedRefNo } from '../../../../utils/helpers';
import { permissionsSelector } from '../../selectors';

const emptyArray = [];

const getCategoryById = (categoryId, store) => (
  helpers.categoryNameForId(categoryId, isUnified(store) ? 'itunes_connect' : store) || '');

const PubTopApps = ({
  settings,
  publisher,
  metric,
  topApps,
  saveQVScrollPosition,
}) => {
  const dispatch = useDispatch();

  const permissions = useSelector(permissionsSelector);

  const topAppsData = topApps[metric] || emptyArray;
  const showData = !settings.error;
  const showEmptyState = !settings.pending && isEmpty(topAppsData) && !settings.error;

  const tableColumns = useMemo(() => ([
    { dataField: 'apps', text: 'Primary Apps', style: { width: 100 } },
    { dataField: 'category', text: 'Category', style: { width: 70 } },
    { dataField: 'release_date', text: 'Release Date', style: { width: 60 } },
    { dataField: 'rating', text: 'Rating', style: { width: 50 } },
    { dataField: metric, text: getMetricName(metric), style: { width: 80 } },
  ]), [metric]);

  const rows = useMemo(() => {
    const onAppQuickViewOpen = (app) => {
      saveQVScrollPosition();
      dispatch(openAppQuickView(app));
    };

    return topAppsData.map((item) => {
      // Temporary ref_no availability check
      const app = item.ref_no ? item : { ...item, ...parseUnifiedRefNo(item['unified-app-id']) };

      return ({
        apps: (
          <MediaObject
            name={app.name}
            app_id={app.ref_no}
            kind={app.kind}
            store={app.store}
            icon_url={app.icon_url}
            appIconSize={24}
            publisher_name={publisher.name}
            publisher_id={publisher.ref_no}
            publisher_store={publisher.store}
            noPublisherData
            appIconBordered
            withoutStore
            disablePublisherLink
            appQuickViewAllowed={permissions.allowAppQuickView}
            disableLink={!!app.not_allowed}
            onAppQuickViewOpen={onAppQuickViewOpen}
          />),
        category: <div className="td-category text-truncate">{getCategoryById(app.category_id, app.store)}</div>,
        release_date: format(app.released_on, 'MM/DD/YY'),
        rating: app.avg_rating,
        [metric]: app.not_allowed ? <div className="hicon-lock" /> : valueFormatter(app[metric], metric),
      });
    });
  }, [topAppsData]);

  const skeletonRows = useMemo(() => skeletonTable({
    columns: ['apps', 'category', 'release_date', 'rating', [metric]],
    height: 24,
  }), [metric]);

  return (
    <div className="prospects-sidebar__top-apps prospects-sidebar__box">
      <h2 className="top-apps-title text-black h4">Top Apps by {getMetricName(metric)}</h2>
      {showData && (
        <Fragment>
          <SortableTable
            tableClassName={cx('publisher-table table-layout-fixed', settings.pending && 'publisher-table__pending')}
            theadClassName="publisher-table-thead"
            tbodyClassName="publisher-table-tbody"
            rows={settings.pending ? skeletonRows : rows}
            columns={tableColumns}
            emptyStateText="No data to display"
            showMoreCellColSpan={tableColumns.length}
            initialRowsLimit={skeletonRows.length}
            hasShowMoreCell={false}
          />
          <TotalCount publisher={publisher} totalCount={publisher.total_apps} />
        </Fragment>
      )}
      {showEmptyState && <EmptyBox text="No Apps detected" />}
    </div>
  );
};

PubTopApps.propTypes = {
  settings: T.object.isRequired,
  publisher: T.object.isRequired,
  metric: T.string.isRequired,
  topApps: T.object.isRequired,
  saveQVScrollPosition: T.func.isRequired,
};

export default PubTopApps;
